import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import api from '../../services/api';
import { StatCard, LoadingSpinner, StatusBadge } from '../../components/UI';

export default function CitizenDashboard() {
  const { user } = useSelector(s => s.auth);
  const [stats, setStats] = useState(null);
  const [notices, setNotices] = useState([]);
  const [recentApps, setRecentApps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/dashboard/stats'),
      api.get('/notices/public'),
      api.get('/applications/my'),
    ]).then(([s, n, a]) => {
      setStats(s.stats);
      setNotices(n.notices?.slice(0,3) || []);
      setRecentApps(a.applications?.slice(0,5) || []);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner size="lg" className="py-20" />;

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <div className="rounded-2xl text-white p-6 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1a365d 0%, #2b6cb0 100%)' }}>
        <div className="absolute right-0 top-0 w-32 h-32 bg-white/5 rounded-full -mr-10 -mt-10" />
        <div className="absolute right-10 bottom-0 w-20 h-20 bg-white/5 rounded-full -mb-10" />
        <h2 className="text-xl font-bold mb-1">Welcome, {user?.name || user?.mobile}! 👋</h2>
        <p className="text-blue-200 text-sm">Apply for government NOC certificates online</p>
        <Link to="/user/apply" className="mt-4 inline-flex items-center gap-2 bg-white text-blue-800 font-semibold px-5 py-2 rounded-lg text-sm hover:bg-blue-50 transition-colors">
          📝 Apply for NOC
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard title="Total" value={stats?.total} icon="📋" color="blue" />
        <StatCard title="Pending" value={stats?.pending} icon="⏳" color="yellow" />
        <StatCard title="Approved" value={stats?.approved} icon="✅" color="green" />
        <StatCard title="Rejected" value={stats?.rejected} icon="❌" color="red" />
        <StatCard title="Objection" value={stats?.objection} icon="⚠️" color="orange" />
        <StatCard title="Today" value={stats?.today} icon="📅" color="purple" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Applications */}
        <div className="lg:col-span-2 card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-800">Recent Applications</h3>
            <Link to="/user/applications" className="text-sm text-blue-600 hover:underline">View All</Link>
          </div>
          {recentApps.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-4xl mb-3">📂</p>
              <p className="text-gray-500 text-sm">No applications yet</p>
              <Link to="/user/apply" className="btn-primary mt-3 inline-block text-sm">Apply Now</Link>
            </div>
          ) : (
            <div className="space-y-3">
              {recentApps.map(app => (
                <Link key={app.id} to={`/user/applications/${app.id}`}
                  className="flex items-center justify-between p-3 border border-gray-100 rounded-xl hover:border-blue-200 hover:bg-blue-50 transition-all">
                  <div>
                    <p className="font-medium text-sm text-gray-800">{app.noc_name}</p>
                    <p className="text-xs text-gray-500">{app.application_no}</p>
                  </div>
                  <StatusBadge status={app.status} />
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Notices */}
        <div className="card">
          <h3 className="font-semibold text-gray-800 mb-4">📢 Notices</h3>
          {notices.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">No notices</p>
          ) : (
            <div className="space-y-3">
              {notices.map(n => (
                <div key={n.id} className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="font-medium text-sm text-yellow-800">{n.title}</p>
                  <p className="text-xs text-yellow-700 mt-1 line-clamp-2">{n.message}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card">
        <h3 className="font-semibold text-gray-800 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { to: '/user/apply', icon: '📝', label: 'Apply NOC', color: 'bg-blue-50 text-blue-700 border-blue-200' },
            { to: '/user/applications', icon: '📋', label: 'My Applications', color: 'bg-green-50 text-green-700 border-green-200' },
            { to: '/user/notifications', icon: '🔔', label: 'Notifications', color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
            { to: '/user/download', icon: '⬇️', label: 'Download NOC', color: 'bg-purple-50 text-purple-700 border-purple-200' },
          ].map(a => (
            <Link key={a.to} to={a.to}
              className={`flex flex-col items-center gap-2 p-4 border rounded-xl text-center hover:shadow-md transition-all ${a.color}`}>
              <span className="text-2xl">{a.icon}</span>
              <span className="text-xs font-medium">{a.label}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
